import { useEffect, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness, useNow, useWrite } from "../api/hooks";
import { ApiProblem } from "../api/problem";
import type { Casualties, IncidentStatus, IncidentSummary, Severity } from "../api/types";
import { can, holdsWard, ROLE } from "../auth/roles";
import { StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { FileLinks } from "../components/Files";
import { ACTIVE_INCIDENT_STATUSES, INCIDENT_STATUS, REPORT_STATUS, SEVERITY, SEVERITY_ORDER, statusOf } from "../components/labels";
import { Notice, ProblemAlert, QueryState, StaleNotice, UnknownUnkeyed } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { formatDateTime, formatSeconds, shortId } from "../format/datetime";
import { showMoney } from "../format/money";
import { alertToDispatch, IncidentDetail, IncidentFlags } from "./IncidentDetail";
import { personLabel, reasonError, Row, ScreenHeader, useConfigNames, useWardNames } from "./shared";
import { Button, Chip } from "@lcc/ui";

const people = (c: Casualties | null | undefined) => (c ? `${c.civilians} civilian(s), ${c.firefighters} firefighter(s)` : "—");

/**
 * Why this person may not decide this report (UI only; the server enforces): a local session, someone who wrote or
 * submitted it (four-eyes), an incident outside every ward (the chief only), or a fire officer outside its ward.
 */
export function decideBlock(
  report: { author_id: string; editors: string[]; submitted_by?: string | null; ward_id?: string | null },
  principal: { roles: string[]; wardIds: string[]; partyId: string; auth?: "realm" | "local_auth" },
): string | null {
  if (principal.auth === "local_auth") return "Reports are approved with a normal sign-in (closing can wait until it works again).";
  const me = principal.partyId;
  if (report.author_id === me || report.submitted_by === me || report.editors.includes(me))
    return "You wrote or submitted this report, so a different person (a fire officer of the ward or the chief) decides it.";
  const chief = principal.roles.includes(ROLE.chief);
  if (!report.ward_id && !chief) return "This incident is outside every ward: only the chief approves its report.";
  if (!chief && !holdsWard(principal, report.ward_id)) return "This incident is outside your wards: a fire officer of its ward or the chief decides.";
  return null;
}

/** The incident report and its four-eyes decision (approve closes the incident; return needs a reason). */
export function ReportPanel({ incidentId, incidentStatus }: { incidentId: string; incidentStatus: IncidentStatus }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const q = useApi(["report", incidentId], (api) => endpoints.getReport(api, incidentId));
  const [mode, setMode] = useState<"approve" | "return" | null>(null);
  const r = q.data?.data;
  const fresh = useFreshness(q);
  if (q.error instanceof ApiProblem && q.error.status === 404) return <p className="fer-muted">No report has been drafted yet (the crew commander first on scene or a fire officer writes it in the Work app).</p>;
  if (!r) return <QueryState isLoading={q.isLoading} error={q.error} label="the report" />;
  const block = decideBlock(r, principal);
  return (
    <section aria-label="Incident report" className="fer-subsection">
      <h4>
        Incident report <StatusBadge status={statusOf(REPORT_STATUS, r.status)} />
      </h4>
      <StaleNotice {...fresh} error={q.error} what="report" />
      <dl className="fer-dl">
        <Row term="Cause">{names.cause(r.cause)}</Row>
        <Row term="Property">{names.propertyType(r.property_type)}</Row>
        <Row term="Estimated damage">{showMoney(r.estimated_damage)}</Row>
        <Row term="Injured">{people(r.injured)}</Row>
        <Row term="Fatalities">{people(r.fatalities)}</Row>
        <Row term="Rescued">{people(r.rescued)}</Row>
        <Row term="Water used">{r.water_used_litres != null ? `${r.water_used_litres} litres` : "—"}</Row>
        <Row term="Hydrants used">{r.hydrant_ids?.length ? r.hydrant_ids.map(shortId).join(", ") : "—"}</Row>
        <Row term="Premises">{r.premises_id ? shortId(r.premises_id) : "—"}</Row>
        <Row term="Narrative">{r.narrative ?? "—"}</Row>
        <Row term="Photos">
          <FileLinks ids={r.photo_file_ids} noun="Report photo" />
        </Row>
        <Row term="Written by">
          {personLabel(r.author_id, principal.partyId)}; edited by {r.editors.map((e) => personLabel(e, principal.partyId)).join(", ") || "—"}
        </Row>
        {r.submitted_at && (
          <Row term="Submitted">
            {formatDateTime(r.submitted_at)} by {personLabel(r.submitted_by, principal.partyId)}
          </Row>
        )}
        {r.return_reason && <Row term="Returned because">{r.return_reason}</Row>}
        {r.decided_at && (
          <Row term="Decided">
            {formatDateTime(r.decided_at)} by {personLabel(r.decided_by, principal.partyId)} ({r.decided_role}){r.approval_note ? ` – ${r.approval_note}` : ""}
          </Row>
        )}
      </dl>
      {r.status === "submitted" && can(principal, "decideReport") && (
        <>
          {block ? (
            <Notice tone="warning">{block}</Notice>
          ) : (
            <div className="fer-actions fer-actions--start">
              <Button onClick={() => setMode("approve")}>
                Approve (closes the incident)
              </Button>
              <Button variant="secondary" onClick={() => setMode("return")}>
                Return to its authors
              </Button>
            </div>
          )}
          {!block && incidentStatus !== "cleared" && (
            <Notice tone="warning">The incident is not cleared yet: approval waits until every crew is released or stood down.</Notice>
          )}
        </>
      )}
      {mode && <DecideDialog incidentId={incidentId} mode={mode} onClose={() => setMode(null)} />}
    </section>
  );
}

function DecideDialog({ incidentId, mode, onClose }: { incidentId: string; mode: "approve" | "return"; onClose: () => void }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const w = useWrite((api, body: Record<string, unknown>) => endpoints.decideReport(api, incidentId, body as never));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "return") {
      const e1 = reasonError(text);
      if (e1) return setErr(e1);
      setErr(null);
      return w.mutate({ decision: "return", reason: text.trim() }, { onSuccess: onClose });
    }
    setErr(null);
    w.mutate(text.trim() ? { decision: "approve", note: text.trim() } : { decision: "approve" }, { onSuccess: onClose });
  };
  return (
    <Dialog title={mode === "approve" ? "Approve the report" : "Return the report"} onClose={onClose}>
      <form className="fer-form" onSubmit={submit} noValidate>
        {mode === "approve" && <p>Approving closes the incident and publishes its figures (never the narrative).</p>}
        <label className="fer-field">
          <span>{mode === "approve" ? "Note (optional)" : "What must be corrected (at least 3 characters)"}</span>
          <textarea rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        {err && (
          <p className="fer-form-error" role="alert">
            {err}
          </p>
        )}
        <ProblemAlert error={w.error} />
        <UnknownUnkeyed show={w.unknown} />
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={w.isPending}>
            {mode === "approve" ? "Approve" : "Return"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

type Tab = "incidents" | "reports";

/**
 * Incidents with filters (the control room and the chief city-wide, fire officers their wards, crew commanders their
 * own dispatches; never callers), each with its detail and report, and the reports waiting for approval.
 */
export function IncidentsScreen() {
  const { principal, focus } = useFireContext();
  const now = useNow();
  const names = useConfigNames();
  const wardName = useWardNames();
  const [tab, setTab] = useState<Tab>("incidents");
  const [status, setStatus] = useState<string>("active");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [backup, setBackup] = useState(false);
  const [selected, setSelected] = useState<string | null>(focus.incidentId ?? null);
  const [reportOf, setReportOf] = useState<IncidentSummary | null>(null);
  useEffect(() => {
    if (focus.incidentId) setSelected(focus.incidentId);
  }, [focus.incidentId]);
  const statuses: IncidentStatus[] = status === "active" ? [...ACTIVE_INCIDENT_STATUSES] : [status as IncidentStatus];
  const q = useApi(["incidents", { statuses, severity, backup }], (api) =>
    endpoints.listIncidents(api, { status: statuses, severity: severity ? [severity] : undefined, backupRequested: backup || undefined }),
  { keepPrevious: true });
  const submitted = useApi(["incidents", { report: "submitted" }], (api) => endpoints.listIncidents(api, { status: ["cleared"], reportStatus: "submitted" }), {
    enabled: can(principal, "decideReport"),
  });
  const rows = q.data ?? [];
  const tabs = [
    { key: "incidents" as const, label: "Incidents", count: q.data ? rows.length : undefined },
    ...(can(principal, "decideReport") ? [{ key: "reports" as const, label: "Reports to approve", count: submitted.data?.length ?? 0 }] : []),
  ];
  const chosen = rows.find((r) => r.incident_id === selected);

  return (
    <div className="fer-screen">
      <ScreenHeader title="Incidents and reports" />
      <Tabs label="Incidents and reports" tabs={tabs} current={tab} onChange={setTab}>
        {tab === "incidents" ? (
          <>
            <div className="fer-toolbar">
              <label className="fer-field fer-field--inline">
                <span>Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">Active (not closed or cancelled)</option>
                  {Object.entries(INCIDENT_STATUS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="fer-field fer-field--inline">
                <span>Severity</span>
                <select value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
                  <option value="">All</option>
                  {SEVERITY_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY[s].label}
                    </option>
                  ))}
                </select>
              </label>
              <Chip selected={backup} onClick={() => setBackup(!backup)}>
                Backup requested only
              </Chip>
            </div>
            <QueryState isLoading={q.isLoading} error={q.error} label="incidents" />
            {q.data && rows.length === 0 && <p className="fer-muted">No incident matches.</p>}
            <div className={`fer-split${selected ? " fer-split--open" : ""}`}>
              <div className="fer-split__main">
                {rows.length > 0 && (
                  <table className="lcc-table" aria-label={`Incidents (${rows.length})`}>
                    <thead>
                      <tr>
                        <th scope="col">Incident</th>
                        <th scope="col">Status</th>
                        <th scope="col" className="fer-col-opt">
                          Ward
                        </th>
                        <th scope="col" className="fer-num">
                          Alert to dispatch
                        </th>
                        <th scope="col" className="fer-col-opt">
                          Report
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((i) => {
                        const c = alertToDispatch(i, now);
                        return (
                          <tr key={i.incident_id} className={`fer-row--click${i.incident_id === selected ? " fer-row--selected" : ""}`} onClick={() => setSelected(i.incident_id)}>
                            <th scope="row">
                              <span className="fer-cell-stack">
                                <button
                                  type="button"
                                  className="fer-rowlink"
                                  aria-label={`Open incident ${i.reference}`}
                                  aria-current={i.incident_id === selected ? "true" : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelected(i.incident_id);
                                  }}
                                >
                                  {i.reference} – {names.incidentType(i.incident_type)}
                                </button>
                                <span className="fer-sub">First alert {formatDateTime(i.first_alert_at)}</span>
                                <IncidentFlags inc={i} />
                              </span>
                            </th>
                            <td>
                              <span className="fer-cell-stack">
                                <StatusBadge status={statusOf(SEVERITY, i.severity)} />
                                <StatusBadge status={statusOf(INCIDENT_STATUS, i.status)} />
                              </span>
                            </td>
                            <td className="fer-col-opt">{i.location.in_service_area ? wardName(i.location.ward_id) : "Outside every ward"}</td>
                            <td className="fer-num">
                              {i.status === "cancelled" && !i.first_dispatch_at ? "—" : `${formatSeconds(c.seconds)}${c.running && i.status === "open" ? " (running)" : ""}`}
                            </td>
                            <td className="fer-col-opt">
                              <StatusBadge status={statusOf(REPORT_STATUS, i.report_status)} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {selected && (
                <div className="fer-detailpane">
                  <IncidentDetail key={selected} incidentId={selected} onClose={() => setSelected(null)} onReport={() => chosen && setReportOf(chosen)} />
                  {chosen && chosen.report_status !== "none" && <ReportPanel incidentId={selected} incidentStatus={chosen.status} />}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="fer-hint">
              Submitted reports of cleared incidents in your wards (the chief: every ward). Nobody decides a report they wrote,
              edited or submitted.
            </p>
            <QueryState isLoading={submitted.isLoading} error={submitted.error} label="reports" />
            {submitted.data && submitted.data.length === 0 && <p className="fer-muted">No report is waiting.</p>}
            {(submitted.data ?? []).length > 0 && (
              <table className="lcc-table" aria-label="Reports to approve">
                <thead>
                  <tr>
                    <th scope="col">Incident</th>
                    <th scope="col">Ward</th>
                    <th scope="col">Cleared</th>
                    <th scope="col">
                      <span className="lcc-sr-only">Review</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(submitted.data ?? []).map((i) => (
                    <tr key={i.incident_id}>
                      <th scope="row">
                        {i.reference} – {names.incidentType(i.incident_type)}
                      </th>
                      <td>{i.location.in_service_area ? wardName(i.location.ward_id) : "Outside every ward"}</td>
                      <td>{formatDateTime(i.cleared_at)}</td>
                      <td>
                        <Button variant="secondary" size="sm" onClick={() => setReportOf(i)} aria-label={`Review the report of ${i.reference}`}>
                          Review the report
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Tabs>
      {reportOf && (
        <Dialog title={`Report of ${reportOf.reference}`} onClose={() => setReportOf(null)}>
          <ReportPanel incidentId={reportOf.incident_id} incidentStatus={reportOf.status} />
          <div className="fer-actions">
            <Button onClick={() => setReportOf(null)}>
              Close
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
